import { PackageUpgradeModal } from "./components/OfferGate";
import { useFetchHotel } from "./redux/hooks";
import { getOfferCapabilities } from "../../common/offerCapabilities";
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextInput,
  Heading,
  Layer,
  Text,
  TextArea,
  FormField,
  CheckBox,
  RadioButtonGroup,
  Tip,
} from "grommet";
import { Add, CircleInformation, Edit, Trash } from "grommet-icons";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import ProductService from "@services/product.service";
import PhotoService from "@services/photo.service";
import { PhotoUploader, ValidStatusesSelect, AddProductOption } from "./";
import { roundPrice } from "@common/utils";
import { PriceInput } from "@shared";
import "./AddProduct.scss";
import { useTranslation } from "react-i18next";
import ConsumptionScheduleEditor from "./ConsumptionScheduleEditor";

const defaultValues = {
  _id: 0,
  name: "",
  duration: 0,
  price: 0,
  description: "",
  photo: "",
  taxPercent: "",
  tax: "",
  subCategory: "",
  validStatuses: ["prearrival", "checkedin"],
  consumptionSchedule: {},
  options: [],
  active: false,
  actionType: "request",
  externalUrl: "",
};

export default function AddProduct(props) {
  const { t } = useTranslation();
  const { hotel } = useFetchHotel();
  const [upgrade, setUpgrade] = useState(false);
  const initialActive = useRef(false);
  const { category, productId, onClose } = props;
  const [product, setProduct] = useState(defaultValues);
  const [photo, setPhoto] = useState(undefined);
  const [openEditOption, setOpenEditOption] = useState(false);
  const [optionIndex, setOptionIndex] = useState(-1);
  const partnerManaged = Boolean(product.commerceConnection);
  const loxeManagesActivation =
    product.commerceConnection?.activationMode === "loxe";
  const providerManagedFields = product.providerManagedFields || [];
  const isProviderManaged = (field) =>
    providerManagedFields.includes(field) ||
    (product.commerceProvider === "mews" &&
      (field === "consumptionSchedule" ||
        (field === "photo" && product.providerMetadata?.imageUrls?.length) ||
        (field === "validStatuses" && product.providerMetadata?.promotions)));

  function handleInputChange({ target: { name, value } }) {
    var values = { ...product };
    values[name] = value;
    setProduct(values);
  }

  function handleTaxPercentChange({ target: { value } }) {
    var values = { ...product, taxPercent: value };
    if (product.price) values.tax = roundPrice((product.price * value) / 100);
    setProduct(values);
  }

  function handleTaxChange({ target: { value } }) {
    var values = { ...product, tax: value };
    if (product.price)
      values.taxPercent = roundPrice((value / product.price) * 100);
    setProduct(values);
  }

  function handlePriceChange({ target: { value } }) {
    var values = { ...product, price: value };
    if (product.taxPercent)
      values.tax = roundPrice((product.taxPercent * value) / 100);
    setProduct(values);
  }

  function handleCheckboxChange({ target: { name, checked } }) {
    if (name === "active" && checked && !getOfferCapabilities(hotel).upsells) { setUpgrade(true); return; }
    var values = { ...product };
    values[name] = checked;
    setProduct(values);
  }

  const handleAdd = () => {
    if (product.active && !initialActive.current && !getOfferCapabilities(hotel).upsells) { setUpgrade(true); return; }
    if (productId === 0) {
      ProductService.addProduct(category._id, product).then(
        async (response) => {
          const product = response.data && response.data.product;
          if (product) await uploadPhoto(product._id);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          presentToastErrorContent(error);
        },
      );
    } else {
      ProductService.updateProduct(category._id, product).then(
        async () => {
          await uploadPhoto(productId);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          presentToastErrorContent(error);
        },
      );
    }
  };

  const uploadPhoto = async (productId) => {
    if (!photo) return;
    await ProductService.uploadPhoto(productId, photo).then(
      (response) => {
        if (response.data.success) {
          //presentToastSuccess("Photo Updated");
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const editOption = (optionIndex) => {
    setOptionIndex(optionIndex);
    setOpenEditOption(true);
  };

  const removeOption = (optionIndex) => {
    var nextOptions = [...product.options];
    nextOptions.splice(optionIndex, 1);
    setProduct({ ...product, options: nextOptions });
  };

  const handleOptionChanged = (option) => {
    if (!product.options || product.options.length === 0) {
      setProduct({ ...product, options: [option] });
    } else {
      var nextOptions = [...product.options];
      if (optionIndex < 0 || optionIndex >= nextOptions.length)
        nextOptions.push(option);
      else nextOptions.splice(optionIndex, 1, option);
      setProduct({ ...product, options: nextOptions });
    }
    setOpenEditOption(false);
  };

  useEffect(() => {
    if (productId !== 0) {
      presentLoading();
      ProductService.getProduct(productId).then(
        (response) => {
          dismissLoading();
          const product = response.data;
          if (product._id) {
            if (!product.actionType) {
              product.actionType = product.externalUrl ? "url" : "request";
            }
            initialActive.current = Boolean(product.active);
            setProduct(product);
          }
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
        },
      );
    } else {
      initialActive.current = false;
      setProduct(defaultValues);
    }
  }, [productId]);

  const renderMinMax = (option) => {
    var hasMax = option.max > 0 && option.max < option.list.length;
    if (option.min > 0 || hasMax)
      return (
        "(" +
        (option.min > 0 ? "min=" + option.min + (hasMax ? ", " : "") : "") +
        (hasMax ? "max=" + option.max : "") +
        ")"
      );
    return "";
  };

  return (
    <Layer
      position="center"
      onClickOutside={() => onClose(false)}
      onEsc={onClose}
    >
      {upgrade && <PackageUpgradeModal capability="upsells" onClose={() => setUpgrade(false)} />}
      <Box
        pad="medium"
        gap="small"
        width="large"
        overflow="scroll"
        style={{ display: "block", maxHeight: 700 }}
      >
        <Box direction="row" justify="between">
          <Heading level={3} margin="none">
            {productId === 0
              ? t("productDialog.addTitle")
              : t("productDialog.editTitle")}
          </Heading>
          <CheckBox
            name="active"
            toggle
            disabled={partnerManaged && !loxeManagesActivation}
            checked={product.active}
            onChange={handleCheckboxChange}
            label={t("common.active")}
          />
        </Box>
        {partnerManaged && (
          <Box
            background="light-2"
            round="small"
            pad="small"
            direction="row"
            gap="small"
          >
            <CircleInformation size="small" color="brand" />
            <Text size="small">
              {t(
                loxeManagesActivation
                  ? "productDialog.integratedEditHintLoxeActivation"
                  : "productDialog.integratedEditHint",
              )}
            </Text>
          </Box>
        )}
        <Box direction="row" gap="medium">
          <Box gap="medium">
            <Box justify="between" direction="row">
              <Box>
                {category.isMenu && !partnerManaged && (
                  <FormField
                    label={t("productDialog.subCategory")}
                    width="medium"
                  >
                    <TextInput
                      name="subCategory"
                      value={product.subCategory}
                      onChange={handleInputChange}
                    />
                  </FormField>
                )}
                <FormField
                  className={
                    partnerManaged ? "provider-managed-field" : undefined
                  }
                  label={t("productDialog.name")}
                  width="medium"
                >
                  <TextInput
                    name="name"
                    disabled={partnerManaged}
                    value={product.name}
                    onChange={handleInputChange}
                  />
                </FormField>
                {(!category.name || partnerManaged) && (
                  <FormField
                    className={
                      isProviderManaged("validStatuses")
                        ? "provider-managed-field"
                        : undefined
                    }
                    label={t("productDialog.validStatuses")}
                  >
                    <Tip
                      content={
                        isProviderManaged("validStatuses")
                          ? t("productDialog.providerManagedTip")
                          : undefined
                      }
                    >
                      <Box>
                        <ValidStatusesSelect
                          name="validStatuses"
                          currentValue={product.validStatuses}
                          disabled={isProviderManaged("validStatuses")}
                          onChange={handleInputChange}
                        />
                      </Box>
                    </Tip>
                  </FormField>
                )}
              </Box>
              <PhotoUploader
                label={t("productDialog.photoUpload")}
                fitWidth={980}
                fitHeight={600}
                disabled={isProviderManaged("photo")}
                onChange={setPhoto}
                photo={PhotoService.getURL(product.photo) || ""}
              />
            </Box>
            <Box
              className={partnerManaged ? "provider-managed-field" : undefined}
              gap="xsmall"
            >
              <Text>{t("productDialog.description")}</Text>
              <TextArea
                style={{ height: 150 }}
                name="description"
                disabled={partnerManaged}
                value={product.description}
                onChange={handleInputChange}
              />
            </Box>
            {!partnerManaged && (
              <Box gap="xsmall">
                <Text>{t("productDialog.actionButton")}</Text>
                <RadioButtonGroup
                  name="actionType"
                  options={[
                    {
                      label: t("productDialog.actionRequest"),
                      value: "request",
                    },
                    {
                      label: t("productDialog.actionUrl"),
                      value: "url",
                    },
                  ]}
                  value={product.actionType || "request"}
                  onChange={handleInputChange}
                />
                {product.actionType === "url" && (
                  <FormField label={t("productDialog.externalUrl")}>
                    <TextInput
                      name="externalUrl"
                      placeholder="https://..."
                      value={product.externalUrl || ""}
                      onChange={handleInputChange}
                    />
                  </FormField>
                )}
              </Box>
            )}
            <Box
              className={partnerManaged ? "provider-managed-field" : undefined}
              direction="row"
              gap="small"
            >
              <FormField label={t("productDialog.price")}>
                <PriceInput
                  style={{ border: 0 }}
                  name="price"
                  disabled={partnerManaged}
                  value={product.price}
                  onChange={handlePriceChange}
                />
              </FormField>
              {!category.isMenu && (
                <FormField label={t("productDialog.taxRate")}>
                  <TextInput
                    name="taxPercent"
                    disabled={partnerManaged}
                    value={product.taxPercent}
                    onChange={handleTaxPercentChange}
                  />
                </FormField>
              )}
              {!category.isMenu && (
                <FormField label={t("productDialog.taxAmount")}>
                  <PriceInput
                    style={{ border: 0 }}
                    name="tax"
                    disabled={partnerManaged}
                    value={product.tax}
                    onChange={handleTaxChange}
                  />
                </FormField>
              )}
            </Box>
            <Tip
              content={
                isProviderManaged("consumptionSchedule")
                  ? t("productDialog.providerManagedTip")
                  : undefined
              }
            >
              <Box
                className={
                  isProviderManaged("consumptionSchedule")
                    ? "provider-managed-schedule"
                    : undefined
                }
                round="small"
                pad={
                  isProviderManaged("consumptionSchedule")
                    ? "medium"
                    : undefined
                }
              >
                <ConsumptionScheduleEditor
                  value={product.consumptionSchedule}
                  disabled={isProviderManaged("consumptionSchedule")}
                  onChange={(consumptionSchedule) =>
                    setProduct({ ...product, consumptionSchedule })
                  }
                />
              </Box>
            </Tip>
            {category.isMenu && !partnerManaged && (
              <Box gap="small">
                {openEditOption && (
                  <AddProductOption
                    option={optionIndex >= 0 && product.options[optionIndex]}
                    onClose={() => setOpenEditOption(false)}
                    onChange={handleOptionChanged}
                  />
                )}
                <Box direction="row" gap="small">
                  <Text>{t("productDialog.options")}</Text>
                  <Button
                    icon={<Add size="small" />}
                    onClick={() => {
                      editOption(-1);
                    }}
                    primary
                    color="status-ok"
                    className="add-button"
                  />
                </Box>
                <Box>
                  {product.options.map((option, idx) => (
                    <Box style={{ marginBottom: 10 }} key={"option-" + idx}>
                      <Box direction="row" gap="small">
                        <Text weight="bold">{option.name}</Text>
                        {(option.min !== undefined ||
                          option.max !== undefined) && (
                          <Text weight="bold">
                            &nbsp;{renderMinMax(option)}
                          </Text>
                        )}
                        <Button
                          icon={<Edit size="small" />}
                          onClick={() => {
                            editOption(idx);
                          }}
                          primary
                          color="status-warning"
                          className="add-button"
                        />
                        <Button
                          primary
                          color="status-error"
                          icon={<Trash size="small" />}
                          onClick={() => removeOption(idx)}
                          className="add-button"
                        />
                      </Box>
                      <Box>
                        {option.list &&
                          option.list.map((item, idx2) => (
                            <Box
                              direction="row"
                              gap="small"
                              key={"item-" + idx2}
                            >
                              <Text>- {item.name}</Text>
                              {item.price && (
                                <Text weight="bold">
                                  +{roundPrice(item.price, true)}
                                </Text>
                              )}
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          as="footer"
          gap="small"
          direction="row"
          align="center"
          justify="end"
          pad={{ top: "medium", bottom: "small" }}
        >
          <Button
            label={t("common.cancel")}
            onClick={() => onClose(false)}
            color="dark-3"
          />
          <Button
            label={productId === 0 ? t("common.add") : t("common.update")}
            onClick={handleAdd}
            primary
            color="status-ok"
          />
        </Box>
      </Box>
    </Layer>
  );
}

AddProduct.propTypes = {};
AddProduct.defaultProps = {};
